import { useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import blogData from "../../../blog-articles-data.json";
const blogArticles = blogData as Array<{ slug: string; slug_es?: string; slug_sv?: string; [key: string]: any }>;

interface BlogSlugRedirectProps {
  /** The language prefix to redirect to (e.g., "sv" or "en") */
  targetLang: "sv" | "en" | "es";
  /** The blog base path in the target language (e.g., "blogg" for SV, "blog" for EN/ES) */
  targetBlogBase: string;
}

/**
 * Redirects a blog article from one language's slug to the target language's localized slug.
 * Falls back to the blog listing page if the article can't be found.
 */
export default function BlogSlugRedirect({ targetLang, targetBlogBase }: BlogSlugRedirectProps) {
  const [, setLocation] = useLocation();

  // Try to match the slug from the URL path
  // For /sv/blog/:slug pattern
  const [, svBlogParams] = useRoute("/sv/blog/:slug");
  // For /en/blog/:slug pattern (Spanish slug under English prefix)
  const [, enBlogParams] = useRoute("/en/blog/:slug");

  useEffect(() => {
    const slug = svBlogParams?.slug || enBlogParams?.slug;
    
    if (!slug) {
      // No slug found, redirect to blog listing
      const prefix = targetLang === "es" ? "" : `/${targetLang}`;
      setLocation(`${prefix}/${targetBlogBase}`, { replace: true });
      return;
    }

    // Try to find the article by any of its slugs (slug, slug_es, slug_sv)
    const article = blogArticles.find(
      (a) => a.slug === slug || a.slug_es === slug || a.slug_sv === slug
    );

    if (article) {
      // Get the correct slug for the target language
      let targetSlug: string;
      if (targetLang === "sv") {
        targetSlug = article.slug_sv || article.slug;
      } else if (targetLang === "es") {
        targetSlug = article.slug_es || article.slug;
      } else {
        targetSlug = article.slug;
      }

      const prefix = targetLang === "es" ? "" : `/${targetLang}`;
      setLocation(`${prefix}/${targetBlogBase}/${targetSlug}`, { replace: true });
    } else {
      // Article not found, redirect to blog listing
      const prefix = targetLang === "es" ? "" : `/${targetLang}`;
      setLocation(`${prefix}/${targetBlogBase}`, { replace: true });
    }
  }, [svBlogParams, enBlogParams, targetLang, targetBlogBase, setLocation]);

  return null;
}
